import { PasswordToggle } from "@/components/features";
import { ActiveLink } from "@/components/layout/active-link/ActiveLink";
import { CheckBoxDtos, ReCaptchaComponent, RememberMe } from "@/components/ui";
import { FaEnvelope, FaLock, FaRegIdCard } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { LoginFormProps } from "../Types/types";

const LoginLinks = [
    {
        path: "/login/recover",
        text: "¿Olvidaste tu contraseña?",
        title: "Recuperar contraseña",
    },
];

const FormLogin: React.FC<LoginFormProps> = ({
    email,
    password,
    emailError,
    acceptTerms,
    isCheckboxDisabled,
    isSubmitDisabled,
    tipoDocumento,
    numeroDocumento,
    numeroDocumentoError,
    onTipoDocumentoChange,
    onNumeroDocumentoChange,
    onEmailChange,
    onPasswordChange,
    onSubmit,
    onAcceptTermsChange,
    onRecaptchaVerify,
    onRecaptchaError,
    onRecaptchaExpired,
    loading,
    tiposIdentificacion,
    loadingTipos,
    rememberMe,
    onRememberMeChange,
}) => {
    const documentosOptions = loadingTipos
        ? [{ value: "", label: "Cargando tipos..." }]
        : tiposIdentificacion.map((tipo) => ({
            value: tipo.nombre,
            label: tipo.nombreCompleto,
        }));

    return (
        <form
            key="login-form"
            className="flex flex-col gap-5 form-emerge"
            onSubmit={onSubmit}
        >
                                {/* DOCUMENTO */}
                                <div className="relative space-y-2 p-0.5">
                                    <label className="text-lg font-bold block text-black">
                                        Documento de identifiación{" "}
                                        <span className="text-[var(--color-error)]">*</span>
                                    </label>

                                    <div className="relative p-0.5">
                                        <select
                                            className="w-full px-3 py-2 pr-10 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 border-gray-300 focus:ring-blue-400 cursor-pointer appearance-none"
                                            value={tipoDocumento}
                                            onChange={onTipoDocumentoChange}
                                            disabled={loadingTipos}
                                        >
                                            <option value="">
                                                {loadingTipos ? "Cargando..." : "Selecciona tipo"}
                                            </option>
                                            {documentosOptions.map((doc) => (
                                                <option key={doc.value} value={doc.value}>
                                                    {doc.label}
                                                </option>
                                            ))}
                                        </select>
                                        {/* Icono flecha */}
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xl">
                                            <FiChevronDown />
                                        </span>
                                    </div>

                                    {/* DOCUMENTO */}
                                    <div className="relative">
                                        <div className="flex gap-1">
                                            {/* Input número de documento */}
                                            <div className="flex-1 relative">
                                                <input
                                                    title="Número de documento"
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Número de Documento"
                                                    required
                                                    value={numeroDocumento}
                                                    onChange={(e) => {
                                                        const onlyNumbers = e.target.value.replace(/\D/g, "");
                                                        onNumeroDocumentoChange({
                                                            ...e,
                                                            target: { ...e.target, value: onlyNumbers },
                                                        } as React.ChangeEvent<HTMLInputElement>);
                                                    }}
                                                    disabled={!tipoDocumento}
                                                    className={`w-full pl-10 pr-4 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 ${numeroDocumentoError
                                                        ? "border-red-500 focus:ring-red-400"
                                                        : "border-gray-300 focus:ring-blue-400"
                                                        } ${!tipoDocumento ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                                />
                                                <FaRegIdCard className="w-5 h-5 absolute left-3 top-1/2 -translate-y-3 text-gray-400 pointer-events-none" />
                                            </div>
                                        </div>

                                        {numeroDocumentoError && (
                                            <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                                                {numeroDocumentoError}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* CORREO */}
                                <div className="relative p-0.5">
                                    <label className="text-lg sm:text-base font-bold mb-1 block text-black">
                                        Correo electrónico{" "}
                                        <span className="text-[var(--color-error)]">*</span>
                                    </label>
                                    <div className="relative p-0.5">
                                        <input
                                            title="Correo"
                                            type="email"
                                            placeholder="tucorreo@mail.com"
                                            required
                                            value={email}
                                            onChange={onEmailChange}
                                            disabled={!numeroDocumento || !!numeroDocumentoError}
                                            className={`w-full pl-12 pr-4 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 ${emailError
                                                ? "border-red-500 focus:ring-red-400"
                                                : "border-gray-300 focus:ring-blue-400"
                                                } ${!numeroDocumento ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                        />
                                        <FaEnvelope className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {emailError && (
                                        <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                                            {emailError}
                                        </p>
                                    )}
                                </div>

                                {/* CONTRASEÑA */}
                                <div className="relative p-0.5">
                                    <label className="text-lg sm:text-base font-bold mb-1 block text-black">
                                        Contraseña <span className="text-[var(--color-error)]">*</span>
                                    </label>
                                    <div className="relative p-0.5">
                                        <PasswordToggle
                                            label="Contraseña"
                                            placeholder="Contraseña"
                                            required={true}
                                            name="password"
                                            value={password}
                                            onChange={onPasswordChange}
                                            disabled={!email || !!emailError}
                                            className={`w-full pl-12 pr-4 py-3 border rounded-2xl text-xl bg-white
                               focus:outline-none focus:ring-2 transition duration-300 text-gray-600
                               border-gray-300 focus:ring-blue-400
                               ${!email ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                        />
                                        <FaLock className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 mt-2 lg:flex-row lg:items-center lg:justify-between">

                                    {/* IZQUIERDA: CHECKBOX + REMEMBER (EN COLUMNA) */}
                                    <div className="flex flex-col gap-2 items-center sm:items-start w-full lg:w-auto">

                                        <div className="flex items-center gap-2">
                                            <CheckBoxDtos
                                                checked={acceptTerms}
                                                disabled={isCheckboxDisabled}
                                                onChange={onAcceptTermsChange}
                                                required={true}
                                            />
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <RememberMe
                                                checked={rememberMe}
                                                disabled={isCheckboxDisabled}
                                                onChange={onRememberMeChange}
                                            />
                                        </div>
                                    </div>

                                    {/* DERECHA: OLVIDASTE CONTRASEÑA */}
                                    <div className="text-center text-base lg:text-sm font-medium opacity-90 text-[var(--color-primary)] lg:self-center">
                                        <ActiveLink
                                            key={LoginLinks[0].path}
                                            path={LoginLinks[0].path}
                                            text={LoginLinks[0].text}
                                            title={LoginLinks[0].title}
                                        />
                                    </div>
                                </div>
                                <div>
                                    {/* RECAPTCHA - Solo se puede interactuar después de aceptar términos */}
                                    <ReCaptchaComponent
                                        onVerify={onRecaptchaVerify}
                                        onError={onRecaptchaError}
                                        onExpired={onRecaptchaExpired}
                                        disabled={!acceptTerms}
                                    />
                                </div>

                                {/* BOTÓN */}
                                <button
                                    type="submit"
                                    disabled={isSubmitDisabled || loading}
                                    className={`w-full py-3 sm:py-5 font-bold rounded-2xl text-xl transition duration-300 shadow-md mt-2 ${!isSubmitDisabled && !loading
                                        ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-600)] hover:shadow-lg shadow-blue-400/30 cursor-pointer"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed shadow-gray-300/30"
                                        }`}
                                >
                                    {loading ? "Ingresando..." : "Iniciar Sesión"}
                                </button>

        </form>
    );
};

export default FormLogin;
